import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Inbox, Download, Trash2, Loader2, ShieldCheck } from 'lucide-react';

const STATUSES = ['new', 'reviewed', 'contacted', 'discarded'];

export default function AdminTalentPool() {
  const { i18n } = useTranslation();
  const es = (i18n.language || 'es').startsWith('es');
  const L = (a, b) => (es ? a : b);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get('/admin/talent-pool')).data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadCv = async (p) => {
    setBusy(p.id);
    try {
      const r = await api.get(`/admin/talent-pool/${p.id}/cv`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `cv_${p.name.replace(/\s+/g, '_')}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const setStatus = async (p, status) => {
    try {
      await api.patch(`/admin/talent-pool/${p.id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === p.id ? { ...x, status } : x)));
      toast.success(L('Estado actualizado', 'Status updated'));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(L(
      `¿Eliminar a ${p.name} del Talent Pool? Esto retira el consentimiento y elimina sus documentos. La acción queda auditada.`,
      `Remove ${p.name} from the Talent Pool? This withdraws consent and removes their documents. The action is audited.`))) return;
    try {
      await api.delete(`/admin/talent-pool/${p.id}`);
      setItems((xs) => xs.filter((x) => x.id !== p.id));
      toast.success(L('Prospecto eliminado (retiro de consentimiento auditado)', 'Prospect removed (consent withdrawal audited)'));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="admin-talent-pool-page">
      <div className="flex items-center gap-3">
        <Inbox className="h-6 w-6 text-teal-600" />
        <div>
          <h1 className="font-display text-2xl font-bold">{L('Bolsa de Talento CTH', 'CTH Talent Pool')}</h1>
          <p className="text-sm text-muted-foreground">
            {L('Prospectos recibidos desde la página pública. Confidencial — visible solo para CTH Admin; nunca se comparte con clientes sin consentimiento.',
               'Prospects received from the public page. Confidential — CTH Admin only; never shared with clients without consent.')}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5 text-teal-600" />
        {L('Los CV se almacenan cifrados en el repositorio seguro de CTH. Toda descarga y baja queda auditada.',
           'CVs are stored in the secure CTH repository. Every download and removal is audited.')}
      </div>
      {loading ? (
        <div className="py-16 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {L('Cargando…', 'Loading…')}
        </div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-sm text-muted-foreground shadow-card" data-testid="talent-pool-empty">
          {L('Aún no hay prospectos en la bolsa de talento.', 'No prospects in the talent pool yet.')}
        </Card>
      ) : (
        <div className="space-y-3" data-testid="talent-pool-list">
          {items.map((p) => (
            <Card key={p.id} className="p-4 shadow-card flex flex-wrap items-center gap-3" data-testid={`talent-pool-row-${p.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm">{p.name}</p>
                <p className="text-xs text-muted-foreground">
                  {p.email}{p.area ? ` · ${p.area}` : ''} · {L('Recibido', 'Received')} {(p.created_at || '').slice(0, 10)}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-[10px] bg-teal-50 text-teal-700 border-teal-200">
                    {L('Bolsa pública CTH', 'CTH public pool')}
                  </Badge>
                  {p.consent_at && (
                    <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                      {L('Consentimiento', 'Consent')} {(p.consent_at || '').slice(0, 10)}
                    </Badge>
                  )}
                  {p.doc_available === false && (
                    <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-200 bg-amber-50">{L('Sin documento', 'No document')}</Badge>
                  )}
                </div>
              </div>
              <Select value={p.status || 'new'} onValueChange={(v) => setStatus(p, v)}>
                <SelectTrigger className="w-36 h-8 text-xs" data-testid={`talent-pool-status-${p.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {es ? { new: 'Nuevo', reviewed: 'Revisado', contacted: 'Contactado', discarded: 'Descartado' }[s]
                          : s.charAt(0).toUpperCase() + s.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" className="gap-1.5" disabled={busy === p.id}
                onClick={() => downloadCv(p)} data-testid={`talent-pool-cv-${p.id}`}>
                {busy === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} CV
              </Button>
              <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700"
                onClick={() => remove(p)} data-testid={`talent-pool-delete-${p.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
